import React, { useState, useEffect } from 'react';
import firebaseService from '../services/firebaseService';
import GameHeader from './GameHeader';
import ArtStyleSelector from './ArtStyleSelector';
import PlayerList from './PlayerList';
import PromptInput from './PromptInput';
import GuessInput from './GuessInput';
import ImageDisplay from './ImageDisplay';
import RevealOverlay from './RevealOverlay';
import ValidationService from '../services/validationService';
import { auth } from '../config/firebase';
import useTemporaryMessage from '../hooks/useTemporaryMessage';

// Create a single instance of ValidationService
const validationService = new ValidationService();

const Game = ({ gameState, isPromptGiver }) => {
    const [showCopied, setShowCopied] = useState(false);
    const [isPlayersSectionExpanded, setIsPlayersSectionExpanded] = useState(false);
    const [userId, setUserId] = useState(null);
    const [error, showError] = useTemporaryMessage(3000);
    const [showRevealOverlay, setShowRevealOverlay] = useState(false);

    useEffect(() => {
        // Get current user ID from Firebase Auth
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserId(user.uid);
                // Check if user is prompt giver when authenticated
                if (gameState.promptGiver?.id === user.uid) {
                    gameState.onGameUpdate({
                        ...gameState,
                        status: gameState.status === 'generating' ? 'generating' : 'waiting'
                    });
                }
            }
        });

        // Subscribe to game updates
        if (gameState.roomId) {
            firebaseService.subscribeToGame(gameState.roomId, (updatedGame) => {
                if (typeof gameState.onGameUpdate === 'function') {
                    // Ensure prompt giver status is maintained on reconnection
                    if (updatedGame?.promptGiver?.id === userId &&
                        (updatedGame.status === 'waiting' || updatedGame.status === 'generating')) {
                        updatedGame.status = updatedGame.status === 'generating' ? 'generating' : 'waiting';
                    }
                    gameState.onGameUpdate(updatedGame);
                }
            });
        }

        return () => {
            unsubscribe();
            if (gameState.roomId) {
                firebaseService.unsubscribeFromGame(gameState.roomId);
            }
        };
    }, [gameState.roomId, userId]);

    // Add effect to handle reveal state
    useEffect(() => {
        if (gameState.isPromptRevealed && !showRevealOverlay) {
            setShowRevealOverlay(true);
        }
        // Close reveal overlay when moving to next round
        if (gameState.status === 'waiting') {
            setShowRevealOverlay(false);
        }
    }, [gameState.isPromptRevealed, gameState.status]);

    const handleCopySuccess = () => {
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
    };

    const handleStyleChange = async (style) => {
        const validation = validationService.validateArtStyle(style);
        if (!validation.isValid) {
            showError(validation.message);
            return;
        }

        try {
            console.log('Updating art style to:', style);
            console.log('Current settings:', gameState.settings);

            const updatedSettings = {
                ...gameState.settings,
                artStyle: style
            };

            await firebaseService.updateSettings(gameState.roomId, updatedSettings);
            console.log('Art style updated successfully');
        } catch (error) {
            console.error('Failed to update art style:', error);
            showError('Failed to update art style');
        }
    };

    const handlePromptSubmit = async (prompt) => {
        try {
            await firebaseService.submitPrompt(gameState.roomId, prompt.trim());
        } catch (error) {
            showError('Failed to submit prompt: ' + error.message);
        }
    };

    const handleGuessSubmit = async (guess) => {
        if (!userId) {
            showError('Not authenticated');
            return;
        }

        try {
            await firebaseService.submitGuess(gameState.roomId, userId, guess.trim());
        } catch (error) {
            showError('Failed to submit guess: ' + error.message);
        }
    };

    const handleNextRound = async () => {
        try {
            await firebaseService.nextRound(gameState.roomId);
        } catch (error) {
            showError('Failed to start next round: ' + error.message);
        }
    };

    const handleRevealPrompt = async () => {
        try {
            await firebaseService.revealPrompt(gameState.roomId);
        } catch (error) {
            showError('Failed to reveal prompt: ' + error.message);
        }
    };

    const handleRevealClose = () => {
        setShowRevealOverlay(false);
    };

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    if (!gameState) return <div className="loading">Loading...</div>;

    // Convert guesses object to array if it's not already an array
    const guessesList = Array.isArray(gameState.guesses)
        ? gameState.guesses
        : Object.values(gameState.guesses || {});

    // Convert players object to array if it's not already an array
    const playersList = Array.isArray(gameState.players)
        ? gameState.players
        : Object.entries(gameState.players || {}).map(([id, player]) => ({
            id,
            ...player
        }));

    const hasGuessed = guessesList.some(g => g.playerId === userId);

    return (
        <div className="game-container">
            <GameHeader
                roomId={gameState.roomId}
                onCopySuccess={handleCopySuccess}
                showCopied={showCopied}
                currentRound={gameState.currentRound}
            />

            <main className="game-layout">
                <div className="game-main-content">
                    {gameState.status === 'waiting' && isPromptGiver ? (
                        <div className="prompt-creation-container">
                            <div className="creation-scroll-container">
                                <div className="chosen-one-header">
                                    <div className="chosen-one-crown">🎨</div>
                                    <div className="chosen-one-text">
                                        <h2>You're the Chosen Artist!</h2>
                                        <p>Create a prompt that will challenge and inspire your fellow players</p>
                                    </div>
                                </div>
                                <ArtStyleSelector
                                    currentStyle={gameState.settings?.artStyle || 'random'}
                                    onStyleChange={handleStyleChange}
                                />
                            </div>
                            <PromptInput
                                onSubmit={handlePromptSubmit}
                                isActive={!!userId}
                                submitLabel="✨ Create"
                                placeholder="Enter your prompt... (5 words max)"
                            />
                        </div>
                    ) : (
                        <>
                            <ImageDisplay
                                imageUrl={gameState.generatedImage}
                                gameStatus={gameState.status === 'roundEnd' ? 'waiting' : (gameState.currentPrompt && !gameState.generatedImage ? 'generating' : gameState.status)}
                                onError={() => showError('Failed to load image')}
                                isPromptGiver={isPromptGiver}
                                guesses={guessesList}
                                players={playersList}
                                currentPrompt={gameState.currentPrompt || ''}
                                isPromptRevealed={gameState.isPromptRevealed}
                                userId={userId}
                                gameState={gameState}
                            />

                            {/* Show guess input if haven't guessed */}
                            {gameState.status === 'playing' && !isPromptGiver && !hasGuessed && !gameState.isPromptRevealed && (
                                <GuessInput
                                    onSubmit={handleGuessSubmit}
                                    isActive={!!userId}
                                />
                            )}

                            {/* Show submitted guess */}
                            {gameState.status === 'playing' && !isPromptGiver && hasGuessed && !gameState.isPromptRevealed && (
                                <div className="guess-item">
                                    <span className="player-name">Your guess:</span>
                                    <span className="guess-text">{guessesList.find(g => g.playerId === userId)?.guess}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <aside className="game-side-panel">
                    <PlayerList
                        players={playersList}
                        scores={gameState.scores || {}}
                        roundScores={gameState.roundScores || {}}
                        promptGiver={gameState.promptGiver}
                        guesses={guessesList}
                        status={gameState.status}
                        isPromptRevealed={gameState.isPromptRevealed}
                        currentPrompt={gameState.currentPrompt}
                        userId={userId}
                        isExpanded={isPlayersSectionExpanded}
                        onToggleExpand={() => setIsPlayersSectionExpanded(!isPlayersSectionExpanded)}
                        disconnectedPlayerIds={gameState.disconnectedPlayerIds || []}
                    />

                    {(gameState.status === 'playing' || gameState.status === 'roundEnd') && isPromptGiver && (
                        <div className="side-panel-section">
                            {!gameState.isPromptRevealed ? (
                                <button
                                    className="primary-button"
                                    onClick={handleRevealPrompt}
                                    disabled={!userId}
                                >
                                    Show Results 🥁
                                </button>
                            ) : (
                                <button
                                    className="next-round-button"
                                    onClick={handleNextRound}
                                    disabled={!userId}
                                >
                                    Next Round
                                </button>
                            )}
                        </div>
                    )}
                </aside>
            </main>

            <RevealOverlay
                isVisible={showRevealOverlay}
                prompt={gameState.currentPrompt || ''}
                guesses={guessesList}
                players={playersList}
                imageUrl={gameState.generatedImage}
                onClose={handleRevealClose}
                roundScores={gameState.roundScores || {}}
                isPromptGiver={isPromptGiver}
                onNextRound={handleNextRound}
                gameState={gameState}
            />
        </div>
    );
};

export default Game; 